import type { MercadoPagoSubscription } from './mercado-pago';

export const MERCADO_PAGO_CHECKOUT_STORAGE_KEY =
  'oyb_mercado_pago_checkout_context';

export interface MercadoPagoCheckoutBusiness {
  id: string;
  name?: string;
  phone?: string;
}

export interface MercadoPagoCheckoutContext {
  business: MercadoPagoCheckoutBusiness;
  createdAt: string;
  idempotencyKey: string;
  payerEmail: string;
  professionalCount: number;
  subscription: MercadoPagoSubscription | null;
  token: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

function normalizeContext(value: unknown): MercadoPagoCheckoutContext | null {
  if (!isRecord(value) || !isRecord(value.business)) return null;

  const businessId = value.business.id;
  const token = value.token;
  const professionalCount = value.professionalCount;

  if (
    typeof businessId !== 'string' ||
    !businessId.trim() ||
    typeof token !== 'string' ||
    !token.trim() ||
    typeof professionalCount !== 'number' ||
    !Number.isInteger(professionalCount) ||
    professionalCount < 1
  ) {
    return null;
  }

  return {
    business: {
      id: businessId,
      ...(typeof value.business.name === 'string'
        ? { name: value.business.name }
        : {}),
      ...(typeof value.business.phone === 'string'
        ? { phone: value.business.phone }
        : {}),
    },
    createdAt:
      typeof value.createdAt === 'string'
        ? value.createdAt
        : new Date().toISOString(),
    idempotencyKey:
      typeof value.idempotencyKey === 'string' ? value.idempotencyKey : '',
    payerEmail: typeof value.payerEmail === 'string' ? value.payerEmail : '',
    professionalCount,
    subscription: isRecord(value.subscription)
      ? (value.subscription as MercadoPagoSubscription)
      : null,
    token,
  };
}

export function createBillingIdempotencyKey(prefix = 'subscription'): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return `${prefix}-${crypto.randomUUID()}`;
  }

  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function saveMercadoPagoCheckoutContext(
  context: MercadoPagoCheckoutContext
): void {
  if (typeof window === 'undefined') return;

  try {
    window.sessionStorage.setItem(
      MERCADO_PAGO_CHECKOUT_STORAGE_KEY,
      JSON.stringify(context)
    );
  } catch {
    // Checkout can continue even when browser storage is unavailable.
  }
}

export function getMercadoPagoCheckoutContext(): MercadoPagoCheckoutContext | null {
  if (typeof window === 'undefined') return null;

  try {
    const rawValue = window.sessionStorage.getItem(
      MERCADO_PAGO_CHECKOUT_STORAGE_KEY
    );
    if (!rawValue) return null;
    return normalizeContext(JSON.parse(rawValue) as unknown);
  } catch {
    return null;
  }
}

export function updateMercadoPagoCheckoutSubscription(
  subscription: MercadoPagoSubscription | null
): MercadoPagoCheckoutContext | null {
  const context = getMercadoPagoCheckoutContext();
  if (!context) return null;

  const nextContext = { ...context, subscription };
  saveMercadoPagoCheckoutContext(nextContext);
  return nextContext;
}

export function clearMercadoPagoCheckoutContext(): void {
  if (typeof window === 'undefined') return;

  try {
    window.sessionStorage.removeItem(MERCADO_PAGO_CHECKOUT_STORAGE_KEY);
  } catch {
    // Nothing else to clear when storage is unavailable.
  }
}
